import { Modal, message } from 'antd'
import { hasResponseData } from './requestSafety'

export interface IResult<T> {
  data: T
  msg: string
  code: number
}

export interface IData {
  status: number
  msg: string
}

export enum Status {
  ok = 0,
  logout = -1,
  notInGroup = -3,
  photoDeleted = -5,
  photoNotReady = -6,
  notAdmin = -10,
  noCheckPermission = -12,
  verifyCodeError = -17,
  sendVerifyCodeError = -19,
  watermarkDeleted = -23,
  watermarkUpdated = -24,
  overVerifyCodeNumLimit = -27,
  mobileNotSign = -31,
  notInAnyGroup = -32,
  existingTask = -33,
  queryToLong = -34,
  deleteFailed = -40,
  noPermission = -56,
  albumNotExist = -58,
  oneWatermarkKeyword = -59,
  removeSubscribedUser = -67,
  alreadyInDepartment = -102,
  photoLocationNoCity = -109,
  moreThan20Levels = -110,
  moreThan5000Departments = -111,
  removeAllBeforeDeleteDep = -112,
  calculatedPhotoLocation = -113,
  /** 导出选了非缩略图清晰度，但条数超过 1000 */
  moreThan1000OnQualityExport = -115,
  removeWatermarkTaskNotStart = -121,
  removeWatermarkTaskCongestion = -122,
  removeWatermarkTaskFailed = -124,
  batchRemoveWatermarkMoreThanMaxCount = -125,
  vipForbiden = -128,
  svipForbiden = -129,
  downloadRemoveWatermarkCountNotEnough = -130,
  downloadRemoveWatermarkUnsupported = -131,
  moreThan2000OnDownload = -201,
  noPhotoInAlbum = -202,
  noFolderUploadPermission = -203,
  onlyDeleteYourOwnPhotos = -205,
  moreThan2000OnDelete = -206,
  vipRemoveWatermarkCountNotEnough = -402,
  sVipRemoveWatermarkCountNotEnough = -403,
  groupHasNoDepartment = -401,
  photoViewNoPass = -991, //权限中心 照片权限不通过
  departmentNoPass = -992, //权限中心 人员权限不通过
  customerNoPass = -995, //权限中心 客户权限不通过
  inviteMember_1112 = -1112,
  inviteMember_1113 = -1113
}

const exceptionHelper = (result: IResult<IData>) => {
  if (!hasResponseData(result)) {
    // 网络失败或异常接口返回时，保留原始错误，避免继续读取 data.status 产生二次报错。
    return Promise.reject(result)
  }

  const data = result.data

  if (data.status === undefined) {
    //接口返回数据结构不完整
    return Promise.reject(result)
  }
  if (data.status === Status.ok) {
    //成功
    return Promise.resolve(data)
  } else if (data.status === Status.logout) {
    //注销
    Modal.warning({
      title: '提示',
      content: '登录状态失效，请重新登录',
      onOk: () => {
        window.location.href = '/'
      }
    })
  } else if (data.status === Status.notInGroup) {
    //未进群
    localStorage.setItem('group_id', '')
    Modal.warning({
      title: '提示',
      content: '您已退出或被移出此团队',
      onOk: () => {
        window.location.href = '/'
      }
    })
  } else if (data.status === Status.notAdmin) {
    //不是管理员
    Modal.warning({
      title: '提示',
      content: '你的管理员身份已被取消，不可进行此操作'
    })
  } else if (data.status === Status.noCheckPermission) {
    //无查看权限
    Modal.warning({
      title: '提示',
      content: '你无权限查看，可联系管理员为你增加权限'
    })
  } else if (data.status === Status.verifyCodeError) {
    //验证码错误
    Modal.warning({
      title: '提示',
      content: '验证码错误，请检查验证码'
    })
  } else if (data.status === Status.existingTask) {
    //有正在进行的任务
    Modal.info({
      title: '有正在处理中的任务，请等待任务完成之后再试'
    })
  } else if (data.status === Status.queryToLong) {
    //查询子句长度超过1K
    Modal.warning({
      title: '提示',
      content: '筛选条件过多，请缩小筛选范围。'
    })
  } else if (data.status === Status.deleteFailed) {
    Modal.warning({
      title: '提示',
      content: '删除失败，该文件已超过可删除时间。请联系管理员操作'
    })
  } else if (data.status === Status.noPermission) {
    //无操作权限
    Modal.warning({
      title: '提示',
      content: '你没有操作权限，可联系管理员为你增加权限'
    })
  } else if (data.status === Status.albumNotExist) {
    //相册不存在
    Modal.warning({
      title: '提示',
      content: '此相册已被删除'
    })
  } else if (data.status === Status.oneWatermarkKeyword) {
    //创建|编辑相册，只有1个关键字，且只有1个字符
    Modal.warning({
      title: '提示',
      content: '您的水印关键词只有一个字，多写几个字再试试'
    })
  } else if (data.status === Status.alreadyInDepartment) {
    // /web/workgroup/v3/batch/invite/user
    //已在部门中
    return Promise.resolve(data)
  } else if (data.status === Status.removeSubscribedUser) {
    //移除成员中包含开通订阅用户
    return Promise.resolve(data)
  } else if (data.status === Status.moreThan20Levels) {
    message.warning('当前团队已有20级部门，不能再添加')
  } else if (data.status === Status.moreThan5000Departments) {
    message.warning('当前团队已有5000个部门，不能再添加')
  } else if (data.status === Status.removeAllBeforeDeleteDep) {
    message.warning('请先转出部门内所有人再删除部门')
  } else if (data.status === Status.moreThan2000OnDownload) {
    //下载超过2000张
    message.warning('数量超2000张，暂不支持下载，请缩小下载时间范围')
  } else if (data.status === Status.noPhotoInAlbum) {
    //下载超过2000张
    message.warning('文件夹内无照片或选择时间段内无照片')
  } else if (data.status === Status.groupHasNoDepartment) {
    message.warning('此团队没有设置部门，请先设置部门后，再使用此功能')
  } else if (data.status === Status.onlyDeleteYourOwnPhotos) {
    Modal.warning({
      title: '提示',
      content: '删除失败，仅能删除自己的照片。'
    })
  } else if (data.status === Status.moreThan2000OnDelete) {
    Modal.warning({
      title: '提示',
      content: '照片超过10000张，暂不支持删除。'
    })
    return Promise.resolve(data)
  } else if (data.status === Status.departmentNoPass) {
    Modal.warning({
      title: '提示',
      content: '暂无部门查看权限'
    })
  } else if (data.status === Status.customerNoPass) {
    Modal.warning({
      title: '提示',
      content: '暂无查看权限'
    })
  } else if (data.status === Status.photoViewNoPass) {
    Modal.warning({
      title: '提示',
      content: '暂无照片查看权限'
    })
  } else if (
    data.status === Status.inviteMember_1112 ||
    data.status === Status.inviteMember_1113
  ) {
    ///web/workgroup/v3/batch/invite/user
    //邀请的这批手机号全部邀请
    return Promise.resolve(data)
  } else if (data.status === Status.calculatedPhotoLocation) {
    message.warning(data.msg)
    return Promise.resolve(data)
  } else if (data.status === Status.photoLocationNoCity) {
    message.warning('该团队无拍照地点')
    return Promise.resolve(data)
  } else if (
    data.status === Status.removeWatermarkTaskNotStart ||
    data.status === Status.removeWatermarkTaskCongestion ||
    data.status === Status.removeWatermarkTaskFailed ||
    data.status === Status.batchRemoveWatermarkMoreThanMaxCount ||
    data.status === Status.vipRemoveWatermarkCountNotEnough ||
    data.status === Status.sVipRemoveWatermarkCountNotEnough
  ) {
    return Promise.resolve(data)
  } else if (
    //065 团队会员2.0，会员权益调整
    data.status === Status.svipForbiden ||
    data.status === Status.vipForbiden
  ) {
    return Promise.resolve(data)
  }
  return Promise.reject(result)
}

export default exceptionHelper
